import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { pct } from "score-common";
import { useTranslation } from "i18n-fe";

// #3375 (scoreadmin-dashboard workstream) -- the Event Progress bar.
//
// It used to be finalCount / rideCount, so a SCRATCHED ride (in the total,
// never FINAL) pinned the bar below 100% for the rest of the day. Now a ride
// counts as completed when it is final OR scratched OR term'd, and the bar is
// drawn in segments so scratches and term'd rides read as their own colour
// rather than as scored work:
//
//   final     (primary)  -- scored FINAL rides, term'd ones excluded
//   scratched (error)    -- result status SCRATCHED
//   term'd    (warning)  -- FINAL rides with a DR123.3 term reason (No Show,
//                           Excused, Eliminated, Withdrawn); the API reports
//                           these as a subset of finalCount, so the caller
//                           subtracts them out before they reach this component
//
// Each coloured segment has a hover tooltip (label + count) and the key under
// the bar repeats them, with the not-yet-complete remainder.
const SEGMENTS = [
  { id: "final", color: "primary.main" },
  { id: "scratched", color: "error.main" },
  { id: "termd", color: "warning.main" },
];

export default function EventProgressBar({ final = 0, scratched = 0, termd = 0, total = 0 }) {
  const { t } = useTranslation("scoreadmin");
  const counts = { final, scratched, termd };
  const completed = final + scratched + termd;
  const remaining = Math.max(total - completed, 0);
  // The segments share one 100% scale (widths via score-common's shared pct,
  // #1416). Normalise by the larger of total and
  // completed so a transient over-count (a ride counted by the results
  // endpoint before the rides list catches up) never overflows the track.
  const scale = Math.max(total, completed);

  const labels = {
    final: t("dashboard.completion.segments.final"),
    scratched: t("dashboard.completion.segments.scratched"),
    termd: t("dashboard.completion.segments.termd"),
  };
  const summary = t("dashboard.completion.summary", { completed, total });

  return (
    <Box data-testid="event-progress">
      <Typography variant="body1" color="text.primary" gutterBottom data-testid="event-progress-summary">
        {summary}
      </Typography>
      <Box
        role="progressbar"
        aria-label={summary}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={completed}
        data-testid="event-progress-bar"
        sx={{
          display: "flex",
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          bgcolor: "action.disabledBackground",
        }}
      >
        {SEGMENTS.map(({ id, color }) =>
          counts[id] > 0 ? (
            <Tooltip key={id} title={`${labels[id]}: ${counts[id]}`} arrow>
              <Box
                data-testid={`event-progress-segment-${id}`}
                sx={{ width: `${pct(counts[id], scale)}%`, bgcolor: color }}
              />
            </Tooltip>
          ) : null
        )}
      </Box>
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }} data-testid="event-progress-key">
        {SEGMENTS.map(({ id, color }) => (
          <Stack key={id} direction="row" alignItems="center" gap={0.75}>
            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: color }} />
            <Typography variant="body2" color="text.secondary" data-testid={`event-progress-key-${id}`}>
              {labels[id]}: {counts[id]}
            </Typography>
          </Stack>
        ))}
        <Stack direction="row" alignItems="center" gap={0.75}>
          <Box
            sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: "action.disabledBackground" }}
          />
          <Typography variant="body2" color="text.secondary" data-testid="event-progress-key-remaining">
            {t("dashboard.completion.segments.remaining")}: {remaining}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
